import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, from } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { AccountService } from 'src/app/services/auth';
import { MenuService } from 'src/app/services/menu/menu-service';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { Order } from 'src/app/shared/models/orders';
import { MatDialog } from '@angular/material/dialog';
import { CacheWarmupService } from 'src/app/services/offline/cache-warmup.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import {
  AridosDispatchPresentation,
  getAridosDispatchPresentation,
} from '../aridos-dispatch-presentation';

@Component({
    selector: 'app-pedido-pendiente',
    templateUrl: './pedido-pendiente.component.html',
    styleUrls: ['./pedido-pendiente.component.css'],
    standalone: false
})
export class PedidoPendienteComponent implements OnInit, OnDestroy {
  TextoInfo: string = 'Ver más información';
  fechaMinima: Date;
  fechaMaxima: Date;
  obraSeleciona: string = '';
  codigobraSeleciona: string = '';
  orders: Order[] = [];
  ordersOriginal: Order[] = [];
  @ViewChild('miDialogo') miDialogo: any;
  dialogOpacity: number = 0;
  private destroy$ = new Subject<void>();
  /** Evita llamadas duplicadas a getAllOrders cuando warmup y carga inicial coinciden. */
  private readonly loadOrders$ = new Subject<boolean>();
  /** Tras "Actualizar", ignorar ordersUpdated$ del warmup para no pisar forceNetwork. */
  private ignoreWarmupOrdersUpdateUntil = 0;
  constructor(
    private menu: MenuService,
    private readonly orderService: OrderService,
    private accountService: AccountService,
    private dialog: MatDialog,
    private cacheWarmup: CacheWarmupService,
    private syncService: SyncService,
  ) {
    menu.btnPrevius = true;
    menu.tilteMenu = 'Despacho actual';
  }

  isAccordionOpen = false;

  toggleAccordion() {
    this.isAccordionOpen = !this.isAccordionOpen;
    if (this.isAccordionOpen) {
      this.TextoInfo = 'Ver menos información';
    } else {
      this.TextoInfo = 'Ver más información';
    }
  }

  ngOnInit(): void {
    this.loadOrders$
      .pipe(
        debounceTime(80),
        switchMap((forceNetwork) =>
          from(this.orderService.getAllOrders(forceNetwork)).pipe(
            switchMap((orders$) => orders$)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => this.applyOrdersFilter(data));

    // DashboardLayout hace forceWarmUp al navegar; recargar desde caché cuando termine.
    this.cacheWarmup.ordersUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (Date.now() < this.ignoreWarmupOrdersUpdateUntil) {
          return;
        }
        this.loadOrders$.next(false);
      });

    // Pintar de inmediato desde IndexedDB si hay caché.
    this.loadOrders$.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyOrdersFilter(data: Order[]): void {
    const sentBackToDespacho = data.filter(
      (x) => x.flag_pending === '1' && x.rejectionFlag === ''
    );

    const activeTkts = new Set<string>();
    const activeOrders = data.filter((order) => {
      const isActive =
        order.status < 6 &&
        order.status != 0 &&
        order.rejectionFlag == '' &&
        !order.isSignature &&
        !order.isNotSigned &&
        moment(order.orderDate, 'DD/MM/YYYY').isAfter(
          moment().subtract(1, 'days')
        );
      if (isActive) {
        activeTkts.add(order.tkt.toString());
      }
      return isActive;
    });

    this.ordersOriginal = [
      ...activeOrders,
      ...sentBackToDespacho.filter((o) => !activeTkts.has(o.tkt.toString())),
    ];
    this.orders = this.ordersOriginal;
  }

  onFiltrar(event) {
    this.orders = this.ordersOriginal.filter(
      (order) =>
        (order.workName
          .toLowerCase()
          .includes(event.numeroFiltar.toLowerCase()) ||
          order.orderNumber.toString().includes(event.numeroFiltar)) &&
        (event.fechaDesde == null ||
          !moment(event.fechaHasta).isBefore(
            moment(order.orderDate, 'DD/MM/YYYY')
          )) &&
        (event.fechaHasta == null ||
          !moment(event.fechaDesde).isAfter(
            moment(order.orderDate, 'DD/MM/YYYY')
          ))
    );
  }

  onOpenModalFiltro() {
    this.dialogOpacity = 1;
    this.miDialogo.nativeElement.showModal();
  }

  cerrarDialogo() {
    this.dialogOpacity = 0;
    this.miDialogo.nativeElement.close();
  }

  getOrderString(order: Order) {
    return JSON.stringify(order);
  }

  getPresentation(order: Order): AridosDispatchPresentation {
    return getAridosDispatchPresentation(order);
  }

  toAmOrPm(time: string) {
    if (time == null || time == '') return 'No disponible';
    const timeArray = time.split(':');
    const hour = parseInt(timeArray[0]);
    const minute = timeArray[1];
    if (hour >= 12) {
      if (hour > 12) {
        return `${hour - 12}:${minute} PM`;
      }
      return `${hour}:${minute} PM`;
    } else {
      return `${hour}:${minute} AM`;
    }
  }

  isActive() {
    return this.accountService.userValue.ESTADO == '1';
  }

  abrirEnGoogleMaps(direccion: string): void {
    const dir = direccion.replace('.', ' ');
    const query = encodeURIComponent(dir);
    window.location.href = `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  abrirEnWaze(direccion: string): void {
    const dir = direccion.replace('.', ' ');
    const query = encodeURIComponent(dir);
    window.location.href = `https://waze.com/ul?q=${query}&navigate=yes`;
  }

  updateUrl(): void {
    this.ignoreWarmupOrdersUpdateUntil = Date.now() + 4000;
    this.syncService.flushNow();
    this.loadOrders$.next(true);
  }


  // abrirModalConfirmacion() {
  //   const dialogRef = this.dialog.open(ModalConfirmarDatosComponent, {
  //     width: '400px',
  //     disableClose: true
  //   });

  //   dialogRef.afterClosed().subscribe((resultado) => {
  //     if (resultado) {
  //       console.log('✅ Usuario confirmó');
  //     } else {
  //       console.log('❌ Usuario canceló');
  //     }
  //   });
  // }
}

